import { get } from 'svelte/store';
import { settingsStore, settingsActions } from '$lib/stores/settings';
import { bareunApiClient } from '$lib/services/BareunApiClient';
import type { ProviderType, ProviderConfig } from './providers/types';
import { ProviderFactory } from './providers/ProviderFactory';

export interface ApiTestResult {
  success: boolean;
  message: string;
  responseTime?: number;
  serverVersion?: string;
}

export interface ApiConfiguration {
  useCustomServer: boolean;
  serverUrl: string;
  apiKey: string;
  providerType?: ProviderType;
  weakOpt?: 0 | 1;
}

export interface ApiHealthCheckResponse {
  status: string;
  version?: string;
  timestamp?: string;
}

/**
 * Service for managing API configuration and testing
 */
export class ApiConfigurationService {
  private static instance: ApiConfigurationService;
  private readonly DEFAULT_TIMEOUT = 5000; // 5 seconds
  
  static getInstance(): ApiConfigurationService {
    if (!this.instance) {
      this.instance = new ApiConfigurationService();
    }
    return this.instance;
  }

  /**
   * Get provider configuration from API configuration
   */
  private getProviderConfig(config: ApiConfiguration): ProviderConfig {
    if (!config.useCustomServer) {
      return { type: 'proxy' };
    }
    
    const providerType = config.providerType || 'bareun';
    
    if (providerType === 'naraSpeller') {
      return {
        type: 'naraSpeller',
        apiKey: config.apiKey,
        options: {
          weakOpt: config.weakOpt ?? 0
        }
      };
    }
    
    return {
      type: 'bareun',
      serverUrl: config.serverUrl,
      apiKey: config.apiKey
    };
  }

  /**
   * Test connection using provider
   */
  async testConnectionWithProvider(config: ApiConfiguration): Promise<ApiTestResult> {
    const providerConfig = this.getProviderConfig(config);
    const provider = ProviderFactory.createProvider(providerConfig);
    
    try {
      const result = await provider.testConnection();
      return {
        success: result.success,
        message: result.message,
        responseTime: result.responseTime
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : '연결 실패'
      };
    }
  }

  /**
   * Test connection to an API server (legacy method for backward compatibility)
   */
  async testConnection(
    url: string, 
    apiKey: string,
    timeout: number = this.DEFAULT_TIMEOUT
  ): Promise<ApiTestResult> {
    const startTime = Date.now();
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      
      const response = await fetch(`${url}/health`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      const responseTime = Date.now() - startTime;
      
      if (response.ok) {
        try {
          const data: ApiHealthCheckResponse = await response.json();
          return {
            success: true,
            message: '연결 성공!',
            responseTime,
            serverVersion: data.version
          };
        } catch {
          // If JSON parsing fails, still consider it a success
          return {
            success: true,
            message: '연결 성공!',
            responseTime
          };
        }
      } else {
        return {
          success: false,
          message: `연결 실패: ${response.status} ${response.statusText}`,
          responseTime
        };
      }
    } catch (error) {
      const responseTime = Date.now() - startTime;
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          return {
            success: false,
            message: '연결 시간 초과',
            responseTime
          };
        }
        return {
          success: false,
          message: `연결 실패: ${error.message}`,
          responseTime
        };
      }
      
      return {
        success: false,
        message: '알 수 없는 오류가 발생했습니다',
        responseTime
      };
    }
  }

  /**
   * Validate API key format based on provider type
   */
  validateApiKeyFormat(apiKey: string, providerType?: ProviderType): boolean {
    if (!apiKey || apiKey.trim().length === 0) {
      return false;
    }
    
    // Provider-specific validation
    if (providerType === 'naraSpeller') {
      // NaraSpeller keys are typically longer
      return apiKey.length >= 20;
    }
    
    // Check for common API key patterns
    // Bareun API keys typically start with 'koba-'
    if (apiKey.startsWith('koba-')) {
      return apiKey.length > 10;
    }
    
    // Generic validation: at least 10 characters
    return apiKey.length >= 10;
  }

  /**
   * Validate server URL format
   */
  validateServerUrl(url: string): boolean {
    if (!url || url.trim().length === 0) {
      return false;
    }
    
    try {
      const urlObj = new URL(url);
      // Must be http or https
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  }

  /**
   * Get current API configuration
   */
  getCurrentConfiguration(): ApiConfiguration {
    const settings = settingsStore.get();
    
    if (settings.api?.customServer) {
      return {
        useCustomServer: settings.api.customServer.enabled,
        serverUrl: settings.api.customServer.url,
        apiKey: settings.api.customServer.apiKey || '',
        providerType: settings.api.customServer.providerType,
        weakOpt: settings.api.customServer.weakOpt
      };
    }
    
    return {
      useCustomServer: false,
      serverUrl: '',
      apiKey: '',
      providerType: undefined,
      weakOpt: 0
    };
  }

  /**
   * Save API configuration
   */
  saveConfiguration(config: ApiConfiguration): void {
    settingsActions.updateApiSettings({
      customServer: {
        enabled: config.useCustomServer,
        url: config.serverUrl,
        apiKey: config.apiKey,
        providerType: config.providerType as any,
        weakOpt: config.weakOpt
      }
    });
    
    // Update the API client with new settings (for backward compatibility)
    bareunApiClient.updateFromSettings();
    
    // Clear provider cache to force reload with new config
    if (config.providerType) {
      ProviderFactory.clearProvider(config.providerType);
    }
  }

  /**
   * Test current configuration
   */
  async testCurrentConfiguration(): Promise<ApiTestResult> {
    const config = this.getCurrentConfiguration();
    
    if (config.useCustomServer) {
      if (!this.validateServerUrl(config.serverUrl)) {
        return {
          success: false,
          message: '유효하지 않은 서버 URL입니다'
        };
      }
      
      if (!this.validateApiKeyFormat(config.apiKey)) {
        return {
          success: false,
          message: '유효하지 않은 API 키 형식입니다'
        };
      }
      
      return this.testConnection(config.serverUrl, config.apiKey);
    } else {
      // Test default server
      const defaultUrl = settingsActions.getApiUrl();
      const defaultKey = settingsActions.getApiKey();
      
      return this.testConnection(defaultUrl, defaultKey);
    }
  }

  /**
   * Get API status message for UI
   */
  getStatusMessage(result: ApiTestResult): string {
    if (result.success) {
      let message = '✅ 연결 성공';
      if (result.responseTime) {
        message += ` (${result.responseTime}ms)`;
      }
      if (result.serverVersion) {
        message += ` - v${result.serverVersion}`;
      }
      return message;
    } else {
      return `❌ ${result.message}`;
    }
  }

  /**
   * Check if custom server is properly configured
   */
  isCustomServerConfigured(): boolean {
    const config = this.getCurrentConfiguration();
    
    if (!config.useCustomServer) {
      return false;
    }
    
    return this.validateServerUrl(config.serverUrl) && 
           this.validateApiKeyFormat(config.apiKey);
  }

  /**
   * Get server type for display
   */
  getServerType(): 'default' | 'custom' {
    const config = this.getCurrentConfiguration();
    return config.useCustomServer ? 'custom' : 'default';
  }

  /**
   * Reset to default configuration
   */
  resetToDefault(): void {
    this.saveConfiguration({
      useCustomServer: false,
      serverUrl: '',
      apiKey: ''
    });
  }
}

export const apiConfigurationService = ApiConfigurationService.getInstance();